/**
 * The lower-right corner of a listing thumb's price block, which is a UI test
 * (`thumb-location`, utils/ui-tests/registry.ts): the Verified badge, as it
 * has always been, or the listing's location.
 *
 * A listing with no location shows the badge in both arms, so no thumb goes
 * blank, and such a card does not count anyone as exposed: both arms look the
 * same on it.
 */
import Img from './img';
import shared from '../data/js/shared';
import { UiTest } from './ui-tests/UiTest';

const { verified } = shared.product;

interface ListingThumbCornerProps {
  id: string | number;
  trustLevel: number;
  locationText?: string | null;
}

export function VerifiedBadge({ id, trustLevel }: { id: string | number; trustLevel: number }) {
  if (trustLevel < 2) return null;
  return (
    <div className="verified" id={`verified-${id}`} data-testid="listing-verified-badge">
      <div className="verify-badge">
        <Img className="verify-badge-check" src="/images/svg/checkmark.svg" />
      </div>
      <span className="text">{verified}</span>
    </div>
  );
}

export function ThumbLocation({ text }: { text: string }) {
  return (
    <div className="thumb-location" data-testid="listing-location" title={text}>
      {text}
    </div>
  );
}

export default function ListingThumbCorner({ id, trustLevel, locationText }: ListingThumbCornerProps) {
  const place = locationText?.trim();
  return (
    <UiTest
      test="thumb-location"
      exposed={Boolean(place)}
      arms={{
        control: () => <VerifiedBadge id={id} trustLevel={trustLevel} />,
        location: () => (place ? <ThumbLocation text={place} /> : <VerifiedBadge id={id} trustLevel={trustLevel} />),
      }}
    />
  );
}
